import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Mail, Phone, Building2, UserCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamMembers = ({ user }) => {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [leaveStatuses, setLeaveStatuses] = useState({});

  const fetchTeamMembers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to show only active users
      const activeUsers = response.data.filter(u => u.is_active !== false);
      setTeamMembers(activeUsers);
      
      // Fetch leave status for each user
      const statuses = {};
      for (const member of activeUsers) {
        try {
          const leaveRes = await axios.get(`${API_URL}/users/${member.id}/leave-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          statuses[member.id] = leaveRes.data;
        } catch (e) {
          statuses[member.id] = { is_on_leave: false };
        }
      }
      setLeaveStatuses(statuses);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const filteredMembers = teamMembers.filter(member => {
    const query = searchQuery.toLowerCase();
    return (
      member.full_name?.toLowerCase().includes(query) ||
      member.email?.toLowerCase().includes(query) ||
      member.role?.toLowerCase().includes(query) ||
      member.department?.toLowerCase().includes(query)
    );
  });

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getColorFromName = (name) => {
    if (!name) return '#6B7280';
    const colors = ['#7077FE', '#10B981', '#F59E0B', '#EC4899', '#6366F1', '#14B8A6', '#8B5CF6', '#F97316'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      superadmin: '#EF4444',
      ceo: '#7077FE',
      manager: '#10B981',
      team_lead: '#F59E0B',
      team_member: '#3B82F6',
      educator: '#8B5CF6'
    };
    return colors[role] || '#6B7280';
  };

  if (loading) {
    return (
      <div className="content-area" style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading team members...</p>
      </div>
    );
  }

  return (
    <div className="content-area" data-testid="team-members-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Users size={32} color="#7077FE" />
            Team Members
          </h1>
          <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>
            {filteredMembers.length} team member{filteredMembers.length !== 1 ? 's' : ''}
          </p>
        </div>
        
        {/* Search */}
        <div style={{ position: 'relative', minWidth: '280px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input
            type="text"
            placeholder="Search by name, email, role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              border: '1px solid var(--border-color)',
              borderRadius: '10px',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '14px'
            }}
            data-testid="search-team-members"
          />
        </div>
      </div>

      {/* Team Members Grid */}
      {filteredMembers.length === 0 ? (
        <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
          <Users size={48} style={{ marginBottom: '16px', opacity: 0.5, color: 'var(--text-tertiary)' }} />
          <p style={{ color: 'var(--text-tertiary)' }}>No team members found</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {filteredMembers.map(member => {
            const isOnLeave = leaveStatuses[member.id]?.is_on_leave;
            return (
              <div
                key={member.id}
                onClick={() => navigate(`/dashboard/profile/${member.id}`)}
                style={{
                  padding: '20px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  opacity: isOnLeave ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid={`team-member-${member.id}`}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  {/* Avatar */}
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: member.avatar 
                          ? `url(${member.avatar}) center/cover`
                          : `linear-gradient(135deg, ${getColorFromName(member.full_name)}, ${getColorFromName(member.full_name)}dd)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        fontWeight: '600',
                        color: 'white',
                        border: isOnLeave ? '3px solid #3B82F6' : '3px solid var(--bg-primary)',
                        flexShrink: 0
                      }}
                    >
                      {!member.avatar && getInitials(member.full_name)}
                    </div>
                    {isOnLeave && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '-2px',
                          right: '-2px',
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: '#3B82F6',
                          border: '2px solid var(--bg-secondary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px'
                        }}
                        title={`On ${leaveStatuses[member.id]?.leave_type || ''} leave`}
                      >
                        🏖️
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {member.full_name}
                      </h3>
                      {isOnLeave && (
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '600',
                          background: '#DBEAFE',
                          color: '#2563EB'
                        }}>
                          On Leave
                        </span>
                      )}
                    </div>
                    
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: `${getRoleBadgeColor(member.role)}20`,
                      color: getRoleBadgeColor(member.role),
                      textTransform: 'capitalize',
                      marginBottom: '8px'
                    }}>
                      {member.role?.replace('_', ' ')}
                    </span>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                        <Mail size={14} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.email}</span>
                      </div>
                      {member.department && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-tertiary)', fontSize: '12px' }}>
                          <Building2 size={14} />
                          <span>{member.department}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeamMembers;
